import { Dirary } from '../../types/dirary'
import { format, parseISO, addDays } from 'date-fns'
import { ja } from 'date-fns/locale'
// import { isHoliday } from 'holiday-jp';
import { isHoliday } from 'japanese-holidays'
import { calcTotalTimeByDiary } from './calcTotalTime'

export type summaryForOutsourcing = {
  productNo: string //制作番号1
  productBno: string //制作番号2
  date: string //作業開始日
  siteName: string //現場名
  startTime: string //作業開始時間
  endTime: string //作業終了時間
  overTime: number //残業時間
  regularTime: number //定時
  totalWorkingTimeByHour: number //合計作業時間(時間)
  totalWorkingTimeByMinute: number //合計作業時間(分)
  isHoliday: string //日曜・祝日
  weekDay: string //曜日
  supplierId: string //仕入先コード
  supplierShortName: string //仕入先略称
  workerId: string //作業者番号
  workerName: string //作業者名
  remarks: string //備考
}

// 時刻文字列を分単位に変換するヘルパー関数
function timeToMinutes(timeString: string): number {
  if (!timeString) return 0
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + minutes
}

// 分を時刻文字列に変換するヘルパー関数
function minutesToTime(totalMinutes: number): string {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// 日付別の合算データを作成する関数
function createDailySummary(
  diaries: Dirary[],
  fromDate: string,
  toDate: string,
  supplierId: string,
  supplierShortName: string
): summaryForOutsourcing[] {
  const dailySummary: summaryForOutsourcing[] = []

  const startDate = parseISO(fromDate)
  const endDate = parseISO(toDate)

  // 休暇データを抽出
  const holidayDiaries = diaries.filter(
    (d) => d.productNo === '99999999' && d.productBno === '99'
  )

  // 休暇データを日付ごとにグループ化
  const holidayByDate = holidayDiaries.reduce((acc, diary) => {
    if (!acc[diary.workingDate]) {
      acc[diary.workingDate] = []
    }
    acc[diary.workingDate].push(diary)
    return acc
  }, {} as { [key: string]: Dirary[] })

  // 通常の日報データを日付ごとにグループ化（休暇データを除く）
  const diariesByDate = diaries
    .filter((d) => !(d.productNo === '99999999' && d.productBno === '99'))
    .reduce((acc, diary) => {
      if (!acc[diary.workingDate]) {
        acc[diary.workingDate] = []
      }
      acc[diary.workingDate].push(diary)
      return acc
    }, {} as { [key: string]: Dirary[] })

  for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
    const formattedDate = format(date, 'yyyy-MM-dd')
    const diariesForDate = diariesByDate[formattedDate] || []

    const weekDay = format(date, 'eee', { locale: ja })
    const holidayName = isHoliday(date)
    const isHolidayFlag = holidayName || weekDay === '日' ? '〇' : ''

    // 休暇データの確認
    const holidayData = holidayByDate[formattedDate] || []
    const hasHoliday = holidayData.length > 0

    // 備考欄の作成（最初のprocessNameと休暇時間）
    // const holidayRemark = hasHoliday
    //   ? `${holidayData[0].processName} ${holidayData
    //       .reduce((acc, cur) => acc + cur.workingTimeByHour, 0)
    //       .toFixed(1)}`
    //   : ''
    const holidayRemark = hasHoliday
      ? `${holidayData[0].processName}`
      : ''

    if (diariesForDate.length > 0) {
      // 該当日の合計作業時間を計算
      const sumHours = diariesForDate.reduce(
        (acc, cur) => acc + cur.workingTimeByHour,
        0
      )
      const sumMins = diariesForDate.reduce(
        (acc, cur) => acc + cur.workingTimeByMinute,
        0
      )

      const totalWorkingTimeByHour = sumHours
      const totalWorkingTimeByMinute = sumMins

      const regularTime =
        isHolidayFlag === '〇'
          ? 0
          : totalWorkingTimeByHour > 8.0
          ? 8.0
          : totalWorkingTimeByHour
      const overTime =
        isHolidayFlag === '〇'
          ? totalWorkingTimeByHour
          : totalWorkingTimeByHour > 8.0
          ? totalWorkingTimeByHour - 8.0
          : 0.0

      // 該当日の最早開始時刻と最遅終了時刻を求める
      let earliestStartTime = ''
      let latestEndTime = ''

      diariesForDate.forEach((diary) => {
        // 開始時刻の比較
        if (
          !earliestStartTime ||
          timeToMinutes(diary.startTime) < timeToMinutes(earliestStartTime)
        ) {
          earliestStartTime = diary.startTime
        }

        // 終了時刻の計算と比較
        let endTime = diary.endTime
        if (!endTime) {
          // その日報の製作番号に関連する全ての日報を取得
          const sameDateDiaries = diariesForDate.filter(
            (d) =>
              d.productNo === diary.productNo &&
              d.productBno === diary.productBno &&
              d.workingDate === diary.workingDate
          )
          endTime = calcTotalTimeByDiary(
            diary.startTime,
            sameDateDiaries,
            diary.isBreak,
            diary.breakTime
          ).endTime
        }

        if (
          !latestEndTime ||
          timeToMinutes(endTime) > timeToMinutes(latestEndTime)
        ) {
          latestEndTime = endTime
        }
      })

      // 休暇時間がある場合、終了時刻から引く
      if (hasHoliday && latestEndTime) {
        const holidayHours = holidayData.reduce(
          (acc, cur) => acc + cur.workingTimeByHour,
          0
        )
        const totalMinutes = timeToMinutes(latestEndTime)
        const holidayTotalMinutes = holidayHours * 60
        const newTotalMinutes = Math.max(0, totalMinutes - holidayTotalMinutes)
        latestEndTime = minutesToTime(newTotalMinutes)
      }

      const firstDiary = diariesForDate[0]

      dailySummary.push({
        productNo: '', // 空白
        productBno: '', // 空白
        date: formattedDate,
        siteName: '', // 空白
        startTime: earliestStartTime,
        endTime: latestEndTime,
        overTime,
        regularTime,
        totalWorkingTimeByHour,
        totalWorkingTimeByMinute,
        isHoliday: isHolidayFlag,
        weekDay,
        supplierId,
        supplierShortName,
        workerId: firstDiary.workerId,
        workerName: firstDiary.workerName,
        remarks: holidayRemark,
      })
    } else {
      // 該当日にデータがない場合
      const firstDiary = diaries.find(
        (d) => !(d.productNo === '99999999' && d.productBno === '99')
      )

      dailySummary.push({
        productNo: '', // 空白
        productBno: '', // 空白
        date: formattedDate,
        siteName: '', // 空白
        startTime: '',
        endTime: '',
        overTime: 0,
        regularTime: 0,
        totalWorkingTimeByHour: 0,
        totalWorkingTimeByMinute: 0,
        isHoliday: isHolidayFlag,
        weekDay,
        supplierId,
        supplierShortName,
        workerId: firstDiary ? firstDiary.workerId : '',
        workerName: firstDiary ? firstDiary.workerName : '',
        remarks: holidayRemark,
      })
    }
  }

  return dailySummary
}

export function createSummaryForOutsourcing(
  diaries: Dirary[],
  fromDate: string,
  toDate: string,
  supplierId: string,
  supplierShortName: string
): summaryForOutsourcing[][] {
  const result: summaryForOutsourcing[][] = []

  // 1. 最初に日付別の合算データを追加
  const dailySummary = createDailySummary(
    diaries,
    fromDate,
    toDate,
    supplierId,
    supplierShortName
  )
  result.push(dailySummary)

  const startDate = parseISO(fromDate)
  const endDate = parseISO(toDate)

  // 休暇データを抽出
  const holidayDiaries = diaries.filter(
    (d) => d.productNo === '99999999' && d.productBno === '99'
  )

  // 休暇データを日付ごとにグループ化
  const holidayByDate = holidayDiaries.reduce((acc, diary) => {
    if (!acc[diary.workingDate]) {
      acc[diary.workingDate] = []
    }
    acc[diary.workingDate].push(diary)
    return acc
  }, {} as { [key: string]: Dirary[] })

  // 2. 次に従来の製作番号別のデータを追加
  // 通常の日報データをグループ化（休暇データを除く）
  const grouped = diaries
    .filter((d) => !(d.productNo === '99999999' && d.productBno === '99'))
    .reduce((acc, diary) => {
      const key = `${diary.productNo}-${diary.productBno}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(diary)
      return acc
    }, {} as { [key: string]: Dirary[] })

  for (const key in grouped) {
    const group = grouped[key]
    const groupSummary: summaryForOutsourcing[] = []

    for (let date = startDate; date <= endDate; date = addDays(date, 1)) {
      const formattedDate = format(date, 'yyyy-MM-dd')
      const diariesForDate = group.filter(
        (d) => d.workingDate === formattedDate
      )

      const weekDay = format(date, 'eee', { locale: ja })
      const holidayName = isHoliday(date)
      const isHolidayFlag = holidayName || weekDay === '日' ? '〇' : ''

      // 休暇データの確認
      const holidayData = holidayByDate[formattedDate] || []
      const hasHoliday = holidayData.length > 0

      // 備考欄の作成（最初のprocessNameと休暇時間）

      // const holidayRemark = hasHoliday
      // ? `${holidayData[0].processName} ${holidayData
      //     .reduce((acc, cur) => acc + cur.workingTimeByHour, 0)
      //     .toFixed(1)}`
      // : ''
      const holidayRemark = hasHoliday
      ? `${holidayData[0].processName}`
      : ''

      if (diariesForDate.length > 0) {
        const firstDiary = diariesForDate[0]

        // 通常の作業時間の計算（99999999-99以外）
        const sumHours = diariesForDate.reduce(
          (acc, cur) => acc + cur.workingTimeByHour,
          0
        )
        const sumMins = diariesForDate.reduce(
          (acc, cur) => acc + cur.workingTimeByMinute,
          0
        )

        const totalWorkingTimeByHour = sumHours
        const totalWorkingTimeByMinute = sumMins

        const regularTime =
          isHolidayFlag === '〇'
            ? 0
            : totalWorkingTimeByHour > 8.0
            ? 8.0
            : totalWorkingTimeByHour
        const overTime =
          isHolidayFlag === '〇'
            ? totalWorkingTimeByHour
            : totalWorkingTimeByHour > 8.0
            ? totalWorkingTimeByHour - 8.0
            : 0.0

        // 終了時刻の計算（休暇時間を引く）
        let endTime = firstDiary.endTime
          ? firstDiary.endTime
          : calcTotalTimeByDiary(
              firstDiary.startTime,
              diariesForDate,
              firstDiary.isBreak,
              firstDiary.breakTime
            ).endTime

        // 休暇時間がある場合、終了時刻から引く
        if (hasHoliday) {
          const holidayHours = holidayData.reduce(
            (acc, cur) => acc + cur.workingTimeByHour,
            0
          )
          const totalMinutes = timeToMinutes(endTime)
          const holidayTotalMinutes = holidayHours * 60
          const newTotalMinutes = Math.max(
            0,
            totalMinutes - holidayTotalMinutes
          )
          endTime = minutesToTime(newTotalMinutes)
        }

        groupSummary.push({
          productNo: firstDiary.productNo,
          productBno: firstDiary.productBno,
          date: formattedDate,
          siteName: firstDiary.siteName,
          startTime: firstDiary.startTime,
          endTime,
          overTime,
          regularTime,
          totalWorkingTimeByHour,
          totalWorkingTimeByMinute,
          isHoliday: isHolidayFlag,
          weekDay,
          supplierId,
          supplierShortName,
          workerId: firstDiary.workerId,
          workerName: firstDiary.workerName,
          remarks: holidayRemark,
        })
      } else {
        groupSummary.push({
          productNo: group[0].productNo,
          productBno: group[0].productBno,
          date: formattedDate,
          siteName: group[0].siteName,
          startTime: '',
          endTime: '',
          overTime: 0,
          regularTime: 0,
          totalWorkingTimeByHour: 0,
          totalWorkingTimeByMinute: 0,
          isHoliday: isHolidayFlag,
          weekDay,
          supplierId,
          supplierShortName,
          workerId: group[0].workerId,
          workerName: group[0].workerName,
          remarks: holidayRemark,
        })
      }
    }
    result.push(groupSummary)
  }

  return result
}

export type summaryTotals = {
  productNo: string
  productBno: string
  totalWorkingTimeByHour: number
  totalWorkingTimeByMinute: number
  overTime: number
  regularTime: number
}

export function calculateSummaryTotals(
  summaries: summaryForOutsourcing[][]
): summaryTotals[] {
  const result: summaryTotals[] = []

  summaries.forEach((group) => {
    if (group.length === 0) return

    // グループの最初のアイテムから `productNo` と `productBno` を取得
    const { productNo, productBno } = group[0]

    // 合計値を初期化
    let totalWorkingTimeByHour = 0
    let totalWorkingTimeByMinute = 0
    let overTime = 0
    let regularTime = 0

    // グループ内の各アイテムを集計
    group.forEach((item) => {
      totalWorkingTimeByHour += item.totalWorkingTimeByHour
      totalWorkingTimeByMinute += item.totalWorkingTimeByMinute
      overTime += item.overTime
      regularTime += item.regularTime
    })

    // 集計結果を配列に追加
    result.push({
      productNo,
      productBno,
      totalWorkingTimeByHour,
      totalWorkingTimeByMinute,
      overTime,
      regularTime,
    })
  })

  return result
}
